import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import { AppHeader } from '../../components/Layout/AppHeader';
import { BottomNav } from '../../components/BottomNav/BottomNav';
import { WeatherWidget } from '../../components/WeatherWidget/WeatherWidget';
import { PageSpinner } from '../../components/PageSpinner/PageSpinner';
import CalendarPicker from '../../components/CalendarPicker/CalendarPicker';
import { MonthCalendarView, clearMonthCalendarCache } from './MonthCalendarView';
import { getPositionsByDate, getCargoMonthTotals, getCargoByMonth, editCargoComment, getDetailsByCargo } from '../../services/api';
import type { CargoDayTotal } from '../../services/api';
import { cachePositions, getCachedPositions, updateCachedPositions } from '../../services/db';
import { invalidateSwApiCache } from '../../services/swCache';
import { captureError } from '../../services/errorTracking';
import { usePullToRefresh, PullIndicator } from '../../hooks/usePullToRefresh';
import { usePendingCount } from '../../hooks/useOfflineSync';
import type { Position } from '../../types';
import { supplierBadgeOf, isSelfManagedCargo, displayProductNames, productDraftOf } from '../../services/selfManaged';
import { DraftProduct } from '../../components/DraftProduct/DraftProduct';
import { QuickFilterBar } from './QuickFilterBar';
import {
  applyQuickFilters,
  buildQuickFacets,
  effectiveQuickFilters,
  hasActiveQuickFilters,
  loadQuickFilters,
  QUICK_FILTER_DIMS,
  saveQuickFilters,
  EMPTY_QUICK_FILTERS,
  visibleControllerOf,
  type QuickFilters,
} from './quickFilters';
import { track } from '../../services/analytics';
import { useFeatures } from '../../hooks/useFeatures';
import { useChangeFeed } from '../../hooks/useChangeFeed';
import { lastEditorOf } from '../../services/changeFeed';
import { getUsername } from '../../services/storage';
import { getSupplierActivity } from '../../utils/supplierStamp';
import { countryLabel } from '../../services/countries';
import { controllerLabel, ensureControllerNames, useControllerNames } from '../../services/controllerNames';
import './PositionPage.css';

function toDateKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function posLocale() {
  return getDateLocale();
}
function formatDateDisplay(d: Date) {
  return d.toLocaleDateString(posLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function toInputDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Barra de progreso animada
function AnimatedProgress({ value, total }: { value: number; total: number }) {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.min(Math.round((value / total) * 100), 100) : 0;
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    bar.style.width = '0%';
    const raf = requestAnimationFrame(() => {
      bar.style.transition = 'width 700ms ease-out';
      bar.style.width = `${pct}%`;
    });
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  return (
    <div className="progress-section">
      <div className="progress-header">
        <span className="progress-label">{t('positions.containers', { value, total })}</span>
        <span className="progress-pct">{pct}%</span>
      </div>
      <div className="progress-track">
        <div
          ref={barRef}
          className={`progress-fill ${pct >= 100 ? 'progress-fill--complete' : ''}`}
        />
      </div>
    </div>
  );
}

// Dialog de comentarios
function CommentDialog({
  pos,
  onClose,
  onSaved,
}: {
  pos: Position;
  onClose: () => void;
  onSaved: (idCargo: number, comment: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(pos.comment ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await editCargoComment(pos.idCargo, { comment: text });
      onSaved(pos.idCargo, text);
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: unknown }; message?: string };
      const status = e.response?.status ?? '';
      const msg = JSON.stringify(e.response?.data ?? e.message ?? err);
      alert(t('positions.comment.error', { status, msg }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="dialog__title">{t('positions.comment.title')}</h3>
        <textarea
          className="dialog__textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={t('positions.comment.placeholder')}
          autoFocus
        />
        <div className="dialog__actions">
          <button className="dialog-btn dialog-btn--cancel" onClick={onClose}>{t('common.cancel')}</button>
          <button className="dialog-btn dialog-btn--save" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Cache module-level: suma de fardos (packageNumber) por idCargo.
// Evita re-fetch al re-renderizar la lista. Se rellena conforme las cargas
// resuelven sus detalles. Como el SW ya cachea los GET de detalles, los
// fetchs en frío también son rápidos cuando hay conexión, y los repetidos
// reaprovechan esta tabla sin pasar siquiera por el fetch.
const bundleSumCache = new Map<number, number>();
// Sumas de fardos en vuelo a la vez: deja sitio en las 6 conexiones del
// navegador para lo que el usuario está esperando.
const BUNDLE_SUM_CONCURRENCY = 3;

// Al cambiar de usuario, la sesión emite este evento → vaciamos las sumas de
// fardos en memoria (por si hubiera solapamiento de idCargo entre sesiones).
if (typeof window !== 'undefined') {
  window.addEventListener('app:clear-caches', () => bundleSumCache.clear());
}

// Card de carga individual
function PositionCard({
  pos,
  dateStr,
  bundleSum,
  onCommentClick,
}: {
  pos: Position;
  dateStr: string;
  bundleSum: number | null;
  onCommentClick: (pos: Position) => void;
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const features = useFeatures();
  const hasComment = pos.comment && pos.comment.trim().length > 0;
  // En self-managed, productName/factoryName traen los PLACEHOLDER de las
  // maestras y la dirección real no está en factoryAdress sino en la ubicación
  // que grabó el controlador. Se prefieren los campos propios cuando existen;
  // el flujo clásico no tiene ninguno y cae en los de siempre.
  // Ojo con el orden: en self-managed NO se puede caer a productName, porque
  // ahí vive el placeholder "SELF-MANAGED (placeholder)" de la maestra clásica.
  // Sin producto se muestra el proveedor, y si tampoco lo hay, el booking.
  // Y van TODOS los productos (`displayProductNames`): con dos, el segundo
  // viaja aparte y antes no se veía.
  // Sin producto de verdad, la nota que se tecleó en el portal va antes que
  // los respaldos de siempre, marcada como borrador (2026-10-16).
  const draftProduct = productDraftOf(pos);
  const displayProduct = (
    isSelfManagedCargo(pos)
      ? (displayProductNames(pos) || pos.supplierName || pos.bookingPortic || t('positions.card.noProduct'))
      : (displayProductNames(pos) ?? pos.factoryName ?? pos.bookingPortic)
  ) as string;
  const countryText = countryLabel(pos.countryCode as string | undefined, pos.countryName as string | undefined);
  // Proveedor del badge verde. En self-managed manda el texto libre; sin él,
  // la fábrica SOLO si tiene nombre: la de relleno de la maestra (con la que
  // nace la planning) no lo tiene, y así no sale un nombre que no existe. Con
  // nombre es una fábrica de verdad elegida en el portal (desde 2026-09-07 se
  // homologan por X3) y hay que enseñarla igual que en el flujo clásico.
  const displaySupplier = supplierBadgeOf(pos);

  // El correo del controlador viaja DENTRO de factoryAdress, entre paréntesis:
  // lo añade el servidor solo cuando quien mira es admin, con el formato
  // "Dirección física (controlador@email.com)".
  //
  // Se extrae SIEMPRE de ese campo, aunque la dirección que se pinte venga de
  // locationText. Son dos datos distintos compartiendo columna: al anteponer la
  // ubicación del flujo nuevo, factoryAdress dejaba de leerse y el admin se
  // quedaba sin ver de quién era cada carga.
  const factoryAddress = (pos.factoryAdress ?? pos.factoryAddress) as string | undefined;
  const factoryMatch = factoryAddress?.match(/^(.*?)\s*\(\s*(.*?)\s*\)\s*$/);
  // El correo del controlador se lee de su CAMPO PROPIO (`controllerName`), que
  // el backend manda siempre y no obliga a parsear texto.
  //
  // El paréntesis de `factoryAdress` sigue haciendo falta, pero solo como
  // SEÑAL DE PERMISO: el servidor lo añade únicamente cuando quien mira es
  // admin (CargoRepository, "Solo para Admin modificamos FactoryAddress"), y es
  // lo único que distingue a un admin desde aquí. Así el dato deja de depender
  // del formato del texto y la visibilidad no cambia respecto a antes.
  // (No se puede quitar ese paréntesis del backend: la app Android lo usa para
  // su showContact(), en Position.kt.)
  // (visibleControllerOf: el filtro rápido "Controlador" usa la misma regla.)
  const controller = visibleControllerOf(pos);

  // Nombre completo del controlador, con el mismo formato que el portal
  // ("Xavier Porta Controller - correo@aldahra.com"). El diccionario lo sirve
  // el servidor de usuarios, no el de cargas, así que se pide una sola vez y
  // solo cuando hay controlador que mostrar — es decir, solo al admin.
  // Mientras no haya llegado (o si no hay red) se pinta el correo, como antes.
  useControllerNames();
  useEffect(() => {
    if (controller) void ensureControllerNames();
  }, [controller]);
  const controllerText = controller ? controllerLabel(controller) : undefined;

  // La huella del proveedor (rol Supplier de la PWA): el controlador y el admin
  // ven de un vistazo que un proveedor ya ha rellenado algo, quién fue el
  // último y cuándo. El propio proveedor no la necesita (features.supplierStamp).
  const supplierActivity = features.supplierStamp ? getSupplierActivity(pos.supplierEdits, i18n.language) : null;
  useEffect(() => {
    if (supplierActivity) void ensureControllerNames();
  }, [supplierActivity?.who]);

  // Dirección a mostrar: manda la ubicación que grabó el controlador; si no la
  // hay, la de la fábrica ya sin el correo pegado al final.
  const address = ((pos.locationText as string | undefined)?.trim() || undefined)
    ?? (factoryMatch ? factoryMatch[1].trim() : factoryAddress?.trim());

  const openMaps = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!address) return;
    const url = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noreferrer noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="position-card"
      onClick={() => navigate(`/detail/${pos.idCargo}`, { state: { position: pos, date: dateStr } })}
    >
      {/* Título + clima de la fábrica para la fecha seleccionada */}
      <div className="position-card__title">
        <span className="position-card__title-text">
          {draftProduct ? <DraftProduct text={draftProduct} /> : displayProduct}
        </span>
        {/* La dirección ya limpia: antes se le pasaba con el "(correo)" pegado,
            que solo estorba al geocodificar. */}
        <WeatherWidget address={address} date={dateStr} />
      </div>

      {/* Dirección */}
      {address && (
        <div className="position-card__address" onClick={(e) => e.stopPropagation()}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{ flexShrink: 0, color: 'var(--brand-primary)' }}>
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          <div className="position-card__address-info">
            <span className="position-card__address-text">{address}</span>
          </div>
          <button className="address-maps-btn" onClick={openMaps}>{t('positions.card.map')}</button>
        </div>
      )}

      {/* Controlador (solo cuando el API lo incluye entre paréntesis) */}
      {controller && (
        <div className="position-card__controller" onClick={(e) => e.stopPropagation()}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" style={{ flexShrink: 0, color: 'var(--indigo)' }}>
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
          </svg>
          <span className="position-card__controller-text">{controllerText}</span>
        </div>
      )}

      {supplierActivity && (
        <div
          className="position-card__supplier-check"
          onClick={(e) => e.stopPropagation()}
          title={t('positions.card.supplierActivityTitle', { count: supplierActivity.count })}
        >
          <span className="position-card__supplier-check-icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <path d="M4 10.5l4 4 8-9" />
            </svg>
          </span>
          <span className="position-card__supplier-check-text">
            <strong>{t('positions.card.supplierActivity')}</strong> {controllerLabel(supplierActivity.who)}
            {supplierActivity.when && <span className="position-card__supplier-check-when"> · {supplierActivity.when}</span>}
          </span>
        </div>
      )}

      {/* Datos en dos columnas */}
      <div className="position-card__data">
        <div className="position-card__col">
          {pos.bookingPortic && (
            <div className="data-row">
              <span className="data-label">{t('positions.card.booking')}</span>
              <span className="data-value">{pos.bookingPortic}</span>
            </div>
          )}
          {/* El país se muestra en TODAS las cargas, no solo en las del flujo
              nuevo, y traducido al idioma del usuario (la maestra lo guarda
              siempre en español). */}
          {countryText && (
            <div className="data-row">
              <span className="data-label">{t('positions.card.country')}</span>
              <span className="data-value">{countryText}</span>
            </div>
          )}
          <div className="data-row">
            <span className="data-label">{t('positions.card.cargo')}</span>
            <span className="data-value">{pos.idCargo}</span>
          </div>
          {pos.idPlanning != null && (
            <div className="data-row">
              <span className="data-label">{t('positions.card.planning')}</span>
              <span className="data-value">{pos.idPlanning}</span>
            </div>
          )}
          {pos.carrierName && (
            <div className="data-row">
              <span className="data-label">{t('positions.card.carrier')}</span>
              <span className="data-value">{pos.carrierName}</span>
            </div>
          )}
        </div>
        <div className="position-card__col position-card__col--right">
          <div className="data-row data-row--right">
            <span className="data-label">{t('positions.card.planned')}</span>
            <span className="data-value">{pos.totalContainers}</span>
          </div>
          <div className="data-row data-row--right">
            <span className="data-label">{t('positions.card.loaded')}</span>
            <span className="data-value">{pos.totalContainerLoaded}</span>
          </div>
          {/* En self-managed el distintivo muestra el PROVEEDOR: la fábrica es
              un placeholder sin nombre y saldría vacío. */}
          {displaySupplier && <span className="factory-badge">{displaySupplier}</span>}
        </div>
      </div>

      {/* Separador */}
      <div className="position-card__separator" />

      {/* Barra de progreso animada */}
      <AnimatedProgress value={pos.totalContainerLoaded} total={pos.totalContainers} />

      {/* Comentarios — clickable, no navega al detalle */}
      <div
        className="position-card__comment"
        onClick={(e) => { e.stopPropagation(); onCommentClick(pos); }}
      >
        <span className="comment-label">{t('positions.card.comments')}</span>
        <span className={`comment-text ${!hasComment ? 'comment-text--empty' : ''}`}>
          {hasComment ? pos.comment : t('positions.card.noComment')}
        </span>
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{ flexShrink: 0, color: 'var(--text-faint)' }}>
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
        </svg>
      </div>

      {/* Footer: peso total + nº fardos */}
      {(pos.totalContainerDetailWeight != null || bundleSum != null) && (
        <div className="position-card__footer">
          {pos.totalContainerDetailWeight != null && (
            <span className="position-card__metric">
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="currentColor"
                aria-hidden="true"
                className="position-card__metric-icon"
              >
                {/* Pesa de balanza */}
                <path d="M19.78 9.78l-2-7A1 1 0 0016.81 2H7.19a1 1 0 00-.97.78l-2 7A1 1 0 005.19 11H7v9a1 1 0 001 1h8a1 1 0 001-1v-9h1.81a1 1 0 00.97-1.22zM12 8a1.75 1.75 0 110-3.5A1.75 1.75 0 0112 8z" />
              </svg>
              {t('positions.card.weight')} <strong>{(pos.totalContainerDetailWeight as number).toLocaleString(posLocale())} Tn</strong>
            </span>
          )}
          {pos.totalContainerDetailWeight != null && bundleSum != null && (
            <span className="position-card__footer-sep" aria-hidden="true" />
          )}
          {bundleSum != null && (
            <span className="position-card__metric">
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="currentColor"
                aria-hidden="true"
                className="position-card__metric-icon"
              >
                {/* Caja/fardo con tapa y lazo */}
                <path d="M21 8.5L12 3 3 8.5v7L12 21l9-5.5v-7zm-9-3.2l6.5 3.95L12 13.2 5.5 9.25 12 5.3zM5 10.62l6 3.65v5.5l-6-3.65v-5.5zm14 5.5l-6 3.65v-5.5l6-3.65v5.5z" />
              </svg>
              <strong>{bundleSum.toLocaleString(posLocale())}</strong> {t('positions.card.bundles')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const SELECTED_DATE_KEY = 'SELECTED_DATE';
// Día (clave local) en el que se hizo la última selección manual de fecha.
// La selección solo se conserva mientras estemos en ese mismo día; al cruzar
// la medianoche volvemos a la fecha actual.
const SELECTED_DATE_SAVED_ON_KEY = 'SELECTED_DATE_SAVED_ON';
const SEARCH_QUERY_KEY = 'POSITION_SEARCH_QUERY';
const SEARCH_OPEN_KEY = 'POSITION_SEARCH_OPEN';
const SCROLL_KEY = 'POSITION_SCROLL';
// Interruptor self-managed del header. En localStorage (no session): es una
// preferencia de trabajo, no un estado de navegación.
const POSITIONS_SCOPE_KEY = 'POSITIONS_SCOPE';
// Marca que se pone al abrir una carga DESDE el modal del calendario; al volver
// del detalle reabrimos el calendario en ese día (y solo en ese caso).
const CALENDAR_RETURN_KEY = 'POSITION_CALENDAR_RETURN';

function clearSavedDate() {
  localStorage.removeItem(SELECTED_DATE_KEY);
  localStorage.removeItem(SELECTED_DATE_SAVED_ON_KEY);
}

function loadSavedDate(): Date {
  const saved = localStorage.getItem(SELECTED_DATE_KEY);
  const savedOn = localStorage.getItem(SELECTED_DATE_SAVED_ON_KEY);
  // Solo respetamos la selección manual si se hizo hoy. Si es de un día
  // anterior (cruzamos la medianoche), la descartamos y mostramos el día
  // actual.
  if (saved && savedOn === toDateKey(new Date())) {
    const d = new Date(saved);
    if (!isNaN(d.getTime())) return d;
  }
  if (saved) clearSavedDate();
  return new Date();
}

export function PositionPage() {
  const navigate = useNavigate();
  // Esta pantalla ya no consulta los claims directamente: lo que puede hacer el
  // usuario se decide en features (el FAB de crear lo usan también los admin).
  const features = useFeatures();
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(loadSavedDate);

  const changeDate = (d: Date) => {
    // Si el input de fecha llega vacío, `new Date(NaN, ...)` produce una
    // fecha inválida y `toISOString()` lanza RangeError. La ignoramos.
    if (isNaN(d.getTime())) return;
    localStorage.setItem(SELECTED_DATE_KEY, d.toISOString());
    // Marcamos el día en que se hizo la selección para poder descartarla
    // automáticamente al cambiar de día.
    localStorage.setItem(SELECTED_DATE_SAVED_ON_KEY, toDateKey(new Date()));
    // Al cambiar de fecha, el scroll previo ya no es relevante para la
    // nueva lista. Limpiamos para que el usuario empiece arriba.
    sessionStorage.removeItem(SCROLL_KEY);
    window.scrollTo(0, 0);
    // La lista anterior ya no corresponde a la nueva fecha: la vaciamos y
    // marcamos loading para que se muestre el spinner (y no la planning vieja)
    // mientras llegan los datos del nuevo día.
    setPositions([]);
    setLoading(true);
    setSelectedDate(d);
  };
  const [positions, setPositions] = useState<Position[]>([]);
  // Mapa idCargo → suma de packageNumber. Se rellena fetch-en-segundo-
  // plano por cada carga visible (con cache module-level para no repetir).
  // El SW ya cachea los GET de detalles, así que esto es barato.
  const [bundleSums, setBundleSums] = useState<Record<number, number>>(() =>
    Object.fromEntries(bundleSumCache.entries())
  );
  const [loading, setLoading] = useState(true);

  // FAB "Nueva planning" colapsable: solo "+" y se expande (hover en desktop /
  // toque en móvil) con animación. En táctil el 1er toque expande y el 2º navega.
  const [fabExpanded, setFabExpanded] = useState(false);
  const fabPointerType = useRef<string>('mouse');
  const fabCollapseTimer = useRef<number | null>(null);
  const clearFabCollapse = () => {
    if (fabCollapseTimer.current != null) { clearTimeout(fabCollapseTimer.current); fabCollapseTimer.current = null; }
  };
  const onFabPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') { clearFabCollapse(); setFabExpanded(true); }
  };
  const onFabPointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') setFabExpanded(false);
  };
  const onFabPointerDown = (e: React.PointerEvent) => { fabPointerType.current = e.pointerType; };
  const onFabClick = () => {
    // Táctil: primer toque expande (y auto-colapsa); segundo toque navega.
    if (fabPointerType.current !== 'mouse' && !fabExpanded) {
      clearFabCollapse();
      setFabExpanded(true);
      fabCollapseTimer.current = window.setTimeout(() => setFabExpanded(false), 2600);
      return;
    }
    clearFabCollapse();
    navigate('/create-planning');
  };
  useEffect(() => () => { if (fabCollapseTimer.current != null) clearTimeout(fabCollapseTimer.current); }, []);

  // Carga la suma de fardos de cada carga visible y actualiza el state
  // conforme van llegando, para que las cards se rellenen a medida que el dato
  // está listo.
  //
  // Espera a que termine la carga de la lista y va de BUNDLE_SUM_CONCURRENCY en
  // BUNDLE_SUM_CONCURRENCY (sep 2026): antes salían todas a la vez junto a la
  // lista, el navegador solo atiende 6 peticiones por servidor, y al abrir la
  // app la lista (lo que el usuario espera) quedaba en cola detrás de 20 sumas
  // de fardos; varias agotaban su plazo en cola y la cabecera decía "Sin
  // conexión" con la red perfecta.
  useEffect(() => {
    if (loading || positions.length === 0) return;
    let cancelled = false;
    const pending = positions
      .map((p) => p.idCargo)
      .filter((id) => !bundleSumCache.has(id));
    if (pending.length === 0) return;
    const loadOne = async (idCargo: number) => {
      try {
        const res = await getDetailsByCargo(idCargo, true);
        if (cancelled) return;
        const details = Array.isArray(res.data) ? res.data : [];
        const sum = details.reduce(
          (acc: number, d: { packageNumber?: number }) => acc + (Number(d.packageNumber) || 0),
          0
        );
        bundleSumCache.set(idCargo, sum);
        setBundleSums((prev) => ({ ...prev, [idCargo]: sum }));
      } catch { /* sin conexión y sin cache: queda en blanco */ }
    };
    const queue = [...pending];
    const worker = async () => {
      while (!cancelled && queue.length > 0) {
        await loadOne(queue.shift()!);
      }
    };
    for (let i = 0; i < BUNDLE_SUM_CONCURRENCY; i++) void worker();
    return () => { cancelled = true; };
  }, [positions, loading]);
  const [showCalendar, setShowCalendar] = useState(false);
  // Al montar (p.ej. volver del detalle con atrás): si veníamos de una carga
  // abierta DESDE el calendario, reabrimos el calendario en ese día. Se lee en el
  // inicializador (durante el render, antes de cualquier efecto) para no perder la
  // marca por el orden de efectos padre/hijo.
  const [calendarInitialDay, setCalendarInitialDay] = useState<Date | null>(() => {
    const raw = sessionStorage.getItem(CALENDAR_RETURN_KEY);
    if (!raw) return null;
    try {
      const { y, m, d } = JSON.parse(raw) as { y: number; m: number; d: number };
      return new Date(y, m, d);
    } catch { return null; }
  });
  const [showMonthCalendar, setShowMonthCalendar] = useState<boolean>(() =>
    !!sessionStorage.getItem(CALENDAR_RETURN_KEY)
  );
  const [commentPos, setCommentPos] = useState<Position | null>(null);
  // Inicializar búsqueda desde sessionStorage para que sobreviva a la
  // navegación al detalle y vuelta atrás (sessionStorage se borra al
  // cerrar la pestaña, lo que es el comportamiento esperado).
  const [searchOpen, setSearchOpen] = useState<boolean>(
    () => sessionStorage.getItem(SEARCH_OPEN_KEY) === '1'
  );
  // Filtros rápidos: duran la sesión (abrir una carga y volver, cambiar de día).
  const [quickFilters, setQuickFilters] = useState<QuickFilters>(loadQuickFilters);
  useEffect(() => { saveQuickFilters(quickFilters); }, [quickFilters]);
  const [searchQuery, setSearchQuery] = useState<string>(
    () => sessionStorage.getItem(SEARCH_QUERY_KEY) ?? ''
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // Interruptor self-managed del header. Se recuerda entre sesiones (mismo
  // criterio que el resto de toggles de la app) pero solo se APLICA cuando el
  // usuario ve cargas de los dos flujos; si no, filtrar dejaría la pantalla
  // vacía sin que se vea el botón para desactivarlo.
  const [onlySelfManaged, setOnlySelfManaged] = useState<boolean>(
    () => localStorage.getItem(POSITIONS_SCOPE_KEY) === 'selfmanaged'
  );

  // Persistir cambios de búsqueda
  useEffect(() => {
    if (searchQuery) sessionStorage.setItem(SEARCH_QUERY_KEY, searchQuery);
    else sessionStorage.removeItem(SEARCH_QUERY_KEY);
  }, [searchQuery]);
  useEffect(() => {
    if (searchOpen) sessionStorage.setItem(SEARCH_OPEN_KEY, '1');
    else sessionStorage.removeItem(SEARCH_OPEN_KEY);
  }, [searchOpen]);

  const fetchPositions = useCallback(async (date: Date, silent = false) => {
    const key = toDateKey(date);
    // Caché PRIMERO (stale-while-revalidate): si hay algo guardado lo pintamos
    // YA y revalidamos por detrás; el spinner solo aparece si no hay nada que
    // mostrar. Así, en "lie-fi" (red sin salida real), la lista sale al instante
    // desde IndexedDB en vez de quedarse en blanco esperando el timeout. En
    // refresco silencioso no tocamos la pantalla hasta tener la respuesta (evita
    // parpadeo sobre lo ya visible).
    const cached = await getCachedPositions(key);
    const hadCache = !!cached && cached.length > 0;
    if (!silent) {
      if (hadCache) setPositions(cached!);
      else setLoading(true);
    }
    try {
      const res = await getPositionsByDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      // Solo un array vale. Si la API devuelve otra cosa (pasó probando el rol
      // supplier: "positions.some is not a function"), no se pinta ni, peor,
      // se cachea — con la caché envenenada la pantalla seguía rota aun con
      // red. Se registra para que el fallo del backend no pase desapercibido.
      if (!Array.isArray(res.data)) {
        captureError(new Error('GetCargoByDate devolvió algo que no es una lista'), {
          where: 'PositionPage.fetchPositions',
          date: key,
          tipo: typeof res.data,
          muestra: JSON.stringify(res.data).slice(0, 200),
        });
        if (!hadCache && !silent) setPositions([]);
        return;
      }
      const data: Position[] = res.data;
      await cachePositions(key, data);
      setPositions(data);
    } catch (e) {
      captureError(e, { where: 'PositionPage.fetchPositions', date: key });
      // Ya pintamos la caché arriba si la había; si no había y es carga visible,
      // dejamos la lista vacía (empty state). En silencioso no tocamos nada.
      if (!hadCache && !silent) setPositions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga las cargas de un día para la vista calendario, con el MISMO criterio
  // que la lista: se muestran todas. Cae a la cache local si no hay red.
  const loadDayPositions = useCallback(async (date: Date): Promise<Position[]> => {
    const key = toDateKey(date);
    let data: Position[];
    try {
      const res = await getPositionsByDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      data = res.data ?? [];
      cachePositions(key, data).catch(() => {});
    } catch {
      data = (await getCachedPositions(key)) ?? [];
    }
    return data;
  }, []);

  // Totales de contenedores por día del mes (1 petición) para el grid del
  // calendario. month es 1-12.
  const loadMonthTotals = useCallback(async (year: number, month: number): Promise<CargoDayTotal[]> => {
    const res = await getCargoMonthTotals(year, month);
    return res.data ?? [];
  }, []);

  // Todas las cargas del mes con detalle (buscador del calendario), en UNA sola
  // petición (endpoint dedicado del back). El resultado se cachea por mes en
  // MonthCalendarView.
  const loadMonthPositions = useCallback(async (year: number, month: number): Promise<Position[]> => {
    const res = await getCargoByMonth(year, month);
    return res.data ?? [];
  }, []);

  // Render de las cards del día en el modal del calendario: reutiliza las
  // PositionCard reales (misma info que en la lista de Positions).
  const renderCalendarDay = useCallback((dayPositions: Position[], date: Date) => {
    const ds = toInputDate(date);
    return dayPositions.map((pos) => (
      <PositionCard
        key={pos.idCargo}
        pos={pos}
        dateStr={ds}
        bundleSum={bundleSums[pos.idCargo] ?? bundleSumCache.get(pos.idCargo) ?? null}
        onCommentClick={setCommentPos}
      />
    ));
  }, [bundleSums]);

  // Mientras haya un día abierto en el calendario, dejamos una marca; así, si se
  // abre una carga (→ detalle) y se vuelve con atrás, reabrimos ese día. Al cerrar
  // el día o el calendario, se borra (para no reabrirlo en visitas normales).
  const handleCalendarDaySelected = useCallback((day: Date | null) => {
    if (day) {
      sessionStorage.setItem(
        CALENDAR_RETURN_KEY,
        JSON.stringify({ y: day.getFullYear(), m: day.getMonth(), d: day.getDate() })
      );
    } else {
      sessionStorage.removeItem(CALENDAR_RETURN_KEY);
    }
  }, []);

  const closeMonthCalendar = useCallback(() => {
    sessionStorage.removeItem(CALENDAR_RETURN_KEY);
    setCalendarInitialDay(null);
    setShowMonthCalendar(false);
  }, []);

  const scrollRestoredRef = useRef(false);

  // Guardar posición en scroll continuo. Si el usuario navega al detalle
  // antes de que el debounce dispare, persistimos en el cleanup y en
  // pagehide para no perder el último scroll.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pendingY: number | null = null;
    const flush = () => {
      if (pendingY != null) {
        sessionStorage.setItem(SCROLL_KEY, String(pendingY));
        pendingY = null;
      }
    };
    const onScroll = () => {
      pendingY = window.scrollY;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; flush(); }, 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', flush);
    return () => {
      if (timer) clearTimeout(timer);
      flush();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  useEffect(() => { fetchPositions(selectedDate); }, [selectedDate, fetchPositions]);

  // Mantener la fecha sincronizada con el día real. Al cruzar la medianoche
  // —estando la app abierta o al volver de segundo plano— descartamos cualquier
  // selección manual del día anterior y mostramos el día actual. La selección
  // manual solo persiste dentro del mismo día (ver loadSavedDate / changeDate).
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const rollToToday = () => {
      const today = new Date();
      clearSavedDate();
      // Evitamos re-render si ya estamos en el día actual (misma referencia).
      setSelectedDate((prev) => (toDateKey(prev) === toDateKey(today) ? prev : today));
    };

    const schedule = () => {
      if (timeoutId) clearTimeout(timeoutId);
      const now = new Date();
      // Próxima medianoche + 1s de margen para garantizar que ya es el día nuevo.
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
      timeoutId = setTimeout(() => { rollToToday(); schedule(); }, nextMidnight.getTime() - now.getTime());
    };

    // Una PWA suspendida en segundo plano puede no disparar el timeout; al
    // recuperar visibilidad comprobamos si el día ha cambiado.
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const savedOn = localStorage.getItem(SELECTED_DATE_SAVED_ON_KEY);
      if (savedOn !== toDateKey(new Date())) rollToToday();
      schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Cuando la cola pendiente cambia (sync vacía o nuevos encolados),
  // refrescamos la lista de cargas de la fecha en silencio para que el
  // estado refleje lo que el servidor tiene tras la sync. Si la red es
  // lenta, se comporta igual que un pull-to-refresh con red lenta.
  const pendingGlobal = usePendingCount();
  const lastPendingRef = useRef(pendingGlobal);
  useEffect(() => {
    if (pendingGlobal !== lastPendingRef.current) {
      lastPendingRef.current = pendingGlobal;
      fetchPositions(selectedDate, true);
    }
  }, [pendingGlobal, selectedDate, fetchPositions]);

  // El latido de cambios (2026-09-10): si otro (el portal, otro controlador,
  // el proveedor) toca una carga de este día, o crea una, la lista se vuelve a
  // leer en silencio y el calendario, si está abierto, también. Un aviso
  // pequeño dice quién fue; se va solo.
  const [liveNotice, setLiveNotice] = useState<string | null>(null);
  const [calendarRefreshToken, setCalendarRefreshToken] = useState(0);
  const liveNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useChangeFeed(
    { dates: [selectedDate], cargoIds: positions.map((p) => p.idCargo) },
    (page) => {
      clearMonthCalendarCache();
      fetchPositions(selectedDate, true);
      setCalendarRefreshToken((n) => n + 1);
      const who = lastEditorOf(page);
      // Lo mío desde otro sitio (el portal, otro móvil) refresca igual, pero
      // no hace falta decirme a mí mismo quién fue.
      const me = (getUsername() || '').trim().toLowerCase();
      if (who && who.toLowerCase() !== me) {
        void ensureControllerNames();
        setLiveNotice(who);
        if (liveNoticeTimer.current) clearTimeout(liveNoticeTimer.current);
        liveNoticeTimer.current = setTimeout(() => setLiveNotice(null), 6000);
      }
    },
  );
  useEffect(() => () => { if (liveNoticeTimer.current) clearTimeout(liveNoticeTimer.current); }, []);

  const pullRefresh = useCallback(() => {
    // El pull-to-refresh ya invalida la cache del SW (vía fetchPositions →
    // backend). Aquí descartamos también nuestro cache local de sumas de
    // fardos para que si el usuario editó packageNumber en alguna carga,
    // se vuelva a calcular en el siguiente render.
    bundleSumCache.clear();
    setBundleSums({});
    clearMonthCalendarCache();
    return fetchPositions(selectedDate);
  }, [fetchPositions, selectedDate]);
  const pullState = usePullToRefresh(pullRefresh);

  const handleDateChange = (value: string) => {
    const [y, m, d] = value.split('-').map(Number);
    changeDate(new Date(y, m - 1, d));
    setShowCalendar(false);
  };

  const handleRefresh = () => {
    fetchPositions(selectedDate);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => reg.update());
    }
  };

  const handleCommentSaved = (idCargo: number, comment: string) => {
    setPositions((prev) =>
      prev.map((p) => p.idCargo === idCargo ? { ...p, comment: comment } : p)
    );
    // Espejo en la caché para que offline conserve el comentario editado.
    const key = toDateKey(selectedDate);
    updateCachedPositions(key, (list) =>
      list.map((p) => p.idCargo === idCargo ? { ...p, comment } : p)
    ).catch((e) => captureError(e, { where: 'PositionPage.updateCachedPositions', date: key }));
    // Invalidar la caché HTTP del SW para esa fecha — evita que una
    // próxima fetch en red mala devuelva el comentario antiguo.
    invalidateSwApiCache({ dateKey: key });
  };

  // Buscador. Filtra contains case-insensitive sobre todos los campos
  // visibles en la card: producto, fábrica, booking, dirección (que incluye
  // el controlador entre paréntesis), transportista, comentario y números
  // de cargo/planning/contenedores. No toca el cache ni la lógica de fetch.
  const matchesSearch = useCallback((pos: Position, query: string): boolean => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const fields: Array<string | number | undefined> = [
      pos.productName as string | undefined,
      pos.otherProductName as string | undefined,
      pos.factoryName as string | undefined,
      // Self-managed: sin esto, buscar por el producto o el proveedor que se ve
      // en la card no encontraría nada (los de arriba son los placeholder).
      pos.selfManagedProductName as string | undefined,
      // La nota de producto del portal, solo cuando es lo que la card enseña
      // como producto (2026-10-16). En el portal no se busca, pero aquí es lo
      // único que el controlador ve donde va el producto.
      productDraftOf(pos),
      pos.supplierName as string | undefined,
      pos.locationText as string | undefined,
      pos.bookingPortic,
      pos.factoryAdress as string | undefined,
      pos.factoryAddress as string | undefined,
      pos.carrierName,
      pos.countryName as string | undefined,
      pos.controllerName as string | undefined,
      pos.comment,
      pos.idCargo,
      pos.idPlanning,
      pos.totalContainers,
      pos.totalContainerLoaded,
      pos.totalContainerDetailWeight as number | undefined,
      pos.sealVet,
    ];
    return fields.some((v) => {
      if (v == null) return false;
      return String(v).toLowerCase().includes(q);
    });
  }, []);

  // El interruptor solo se ofrece si en el día conviven los dos flujos: con uno
  // solo no habría nada que separar.
  const hasSelfManaged = useMemo(() => positions.some(isSelfManagedCargo), [positions]);
  const hasClassic = useMemo(() => positions.some((p) => !isSelfManagedCargo(p)), [positions]);
  const showScopeToggle = hasSelfManaged && hasClassic;
  const scopeActive = showScopeToggle && onlySelfManaged;

  // Lo que se vería sin escribir nada en el buscador: ya con el interruptor
  // aplicado. Es el "total" del contador de resultados.
  // Antes se ocultaban las cargas self-managed a 0/0 por ser placeholders del
  // portal. Ya no: el controlador puede crear una planning sin rellenar el
  // número de contenedores, y esa regla le hacía desaparecer de la lista lo que
  // acababa de crear. Se muestran todas.
  const scopedPositions = useMemo(
    () => (scopeActive ? positions.filter(isSelfManagedCargo) : positions),
    [positions, scopeActive]
  );

  // Filtros rápidos (pastillas bajo la fecha). Se calculan sobre lo que deja el
  // interruptor self-managed y ANTES del buscador: los dos se combinan.
  const quickOpts = useMemo(() => ({ allowController: features.filterByController }), [features.filterByController]);
  const effectiveQuick = useMemo(() => effectiveQuickFilters(quickFilters, quickOpts), [quickFilters, quickOpts]);
  const quickFacets = useMemo(
    () => buildQuickFacets(scopedPositions, effectiveQuick, quickOpts),
    [scopedPositions, effectiveQuick, quickOpts]
  );
  const quickActive = hasActiveQuickFilters(effectiveQuick);

  const filteredPositions = useMemo(
    () => applyQuickFilters(scopedPositions, effectiveQuick).filter((p) => matchesSearch(p, searchQuery)),
    [scopedPositions, effectiveQuick, searchQuery, matchesSearch]
  );

  // Restaurar scroll cuando la lista ya está renderizada. Esperamos a que
  // filteredPositions tenga contenido para garantizar que la altura del
  // documento permite el scroll guardado; si la búsqueda no devuelve
  // resultados, restauramos al tope.
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (!saved) { scrollRestoredRef.current = true; return; }
    if (filteredPositions.length === 0) {
      scrollRestoredRef.current = true;
      window.scrollTo(0, 0);
      return;
    }
    scrollRestoredRef.current = true;
    const target = Number(saved);
    // Doble rAF para asegurar que el navegador ha calculado la altura final
    // de las cards (incluye imágenes/iconos que entran tras el primer paint).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo(0, target));
    });
  }, [loading, filteredPositions.length]);

  const openSearch = () => {
    setSearchOpen(true);
    // Esperamos a que el input exista en DOM antes de hacer focus.
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };
  const toggleSearch = () => {
    if (searchOpen) closeSearch();
    else openSearch();
  };

  const dateStr = toInputDate(selectedDate);

  const changeQuickFilters = (next: QuickFilters) => {
    // Uso real de cada dimensión (GA4): para saber cuáles sobran.
    for (const dim of QUICK_FILTER_DIMS) {
      if (next[dim].length !== quickFilters[dim].length) {
        track({ name: 'quick_filter_used', params: { dim, selected: next[dim].length } });
      }
    }
    if (next.pending !== quickFilters.pending) {
      track({ name: 'quick_filter_used', params: { dim: 'pending', selected: next.pending ? 1 : 0 } });
    }
    setQuickFilters(next);
  };

  return (
    <div className="position-page">
      <PullIndicator {...pullState} />
      <AppHeader
        showLogo
        title={t('positions.title')}
        beforeLanguage={showScopeToggle ? (
          <button
            type="button"
            className={`app-header__scope${onlySelfManaged ? ' app-header__scope--active' : ''}`}
            onClick={() => {
              const next = !onlySelfManaged;
              setOnlySelfManaged(next);
              localStorage.setItem(POSITIONS_SCOPE_KEY, next ? 'selfmanaged' : 'all');
            }}
            aria-pressed={onlySelfManaged}
            title={t('positions.scopeSelfManagedHint')}
          >
            {t('positions.scopeSelfManaged')}
          </button>
        ) : undefined}
        rightContent={
          <button className="date-toggle-btn" onClick={() => setShowCalendar(!showCalendar)} aria-label={t('positions.selectDate')}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
            </svg>
          </button>
        }
      />

      {/* Barra de carga lineal */}
      <div className={`linear-progress ${loading ? 'linear-progress--active' : ''}`}>
        <div className="linear-progress__bar" />
      </div>

      {/* Selector de fecha */}
      {showCalendar && (
        <div className="date-picker-overlay" onClick={() => setShowCalendar(false)}>
          <div className="date-picker-popup" onClick={(e) => e.stopPropagation()}>
            <label className="date-picker-label">{t('positions.selectDate')}</label>
            <CalendarPicker value={dateStr} onChange={handleDateChange} />
          </div>
        </div>
      )}

      {/* Fecha + buscador + filtros rápidos: UN solo bloque fijo bajo el header
          (oct 2026, lo pidió Daniel: las pastillas a la vista aunque se haga
          scroll). Al ir juntos, su altura se ajusta sola —buscador abierto o
          cerrado, con o sin contador— sin calcular el "top" de cada uno. */}
      <div className="position-sticky-stack">
      {/* Barra de fecha + refresh + buscador */}
      <div className="position-date-bar">
        <span>
          {formatDateDisplay(selectedDate)}
          {liveNotice && (
            <span className="live-notice" role="status" title={liveNotice}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11" aria-hidden="true">
                <path d="M3 10a7 7 0 0 1 12-5M17 10a7 7 0 0 1-12 5M15 2v3h-3M5 18v-3h3" />
              </svg>
              {t('positions.liveUpdatedBy', { who: controllerLabel(liveNotice).split(' - ')[0] })}
            </span>
          )}
        </span>
        <div className="position-date-bar__actions">
          <button
            className="view-calendar-btn"
            onClick={() => { setCalendarInitialDay(null); setShowMonthCalendar(true); }}
            aria-label={t('positions.viewCalendar')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
            </svg>
            <span>{t('positions.viewCalendar')}</span>
          </button>
          <button
            className={`refresh-btn ${searchOpen ? 'refresh-btn--active' : ''}`}
            onClick={toggleSearch}
            aria-label={searchOpen ? t('positions.closeSearch') : t('positions.searchAria')}
          >
            {searchOpen ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            )}
          </button>
          <button className="refresh-btn" onClick={handleRefresh} disabled={loading} aria-label={t('positions.refresh')}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Input de búsqueda — expandible */}
      <div className={`search-bar ${searchOpen ? 'search-bar--open' : ''}`}>
        <div className="search-bar__inner">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" className="search-bar__icon">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            className="search-bar__input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('positions.searchPlaceholder')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
          />
          {searchQuery && (
            <button
              type="button"
              className="search-bar__clear"
              onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
              aria-label={t('positions.clearSearch')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          )}
        </div>
        {searchQuery && (
          <div className="search-bar__count">
            {t('positions.searchCount', { shown: filteredPositions.length, total: scopedPositions.length })}
          </div>
        )}
      </div>

      <QuickFilterBar
        facets={quickFacets}
        filters={effectiveQuick}
        onChange={changeQuickFilters}
        shown={filteredPositions.length}
        total={scopedPositions.length}
      />
      </div>

      {/* Spinner centrado mientras carga la primera tanda de datos. */}
      {loading && positions.length === 0 && <PageSpinner />}

      {/* Con datos guardados en pantalla y la petición aún en vuelo (típico al
          abrir la app tras un rato), la barra fina de arriba pasaba
          desapercibida y parecía que la app no hacía nada. Aviso visible y con
          spinner; aparece con retardo para no parpadear si la red contesta
          enseguida. */}
      {loading && positions.length > 0 && (
        <div className="refreshing-chip" role="status" aria-live="polite">
          <span className="refreshing-chip__spinner" aria-hidden="true" />
          {t('positions.refreshing')}
        </div>
      )}

      {/* Lista */}
      <div className="position-list">
        {/* scopedPositions y no positions: con el interruptor puesto, un día sin
            cargas self-managed es un día vacío, no un día "con datos y búsqueda
            sin resultados". */}
        {!loading && scopedPositions.length === 0 && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="#ccc" width="64" height="64">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
            </svg>
            <p>{t('positions.emptyDay')}</p>
          </div>
        )}

        {!loading && scopedPositions.length > 0 && filteredPositions.length === 0 && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="#ccc" width="56" height="56">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <p>{searchQuery ? t('positions.noResults', { query: searchQuery }) : t('positions.quickFilters.empty')}</p>
            {quickActive && (
              <button type="button" className="qf-empty-clear" onClick={() => changeQuickFilters(EMPTY_QUICK_FILTERS)}>
                {t('positions.quickFilters.clearAll')}
              </button>
            )}
          </div>
        )}

        {filteredPositions.map((pos) => (
          <PositionCard
            key={pos.idCargo}
            pos={pos}
            dateStr={dateStr}
            bundleSum={bundleSums[pos.idCargo] ?? null}
            onCommentClick={setCommentPos}
          />
        ))}
      </div>

      {/* Vista Calendario mes (para clásicos y self-managed) */}
      <MonthCalendarView
        open={showMonthCalendar}
        onClose={closeMonthCalendar}
        initialDate={selectedDate}
        initialSelectedDay={calendarInitialDay}
        onDaySelected={handleCalendarDaySelected}
        loadMonthTotals={loadMonthTotals}
        loadDay={loadDayPositions}
        loadMonthPositions={loadMonthPositions}
        matchesSearch={matchesSearch}
        scopeFilter={scopeActive ? isSelfManagedCargo : undefined}
        renderDay={renderCalendarDay}
        refreshToken={calendarRefreshToken}
      />

      {/* Dialog comentario */}
      {commentPos && (
        <CommentDialog
          pos={commentPos}
          onClose={() => setCommentPos(null)}
          onSaved={handleCommentSaved}
        />
      )}

      {/* También para el admin: crear plannings del flujo nuevo es una
          capacidad suya, aunque no tenga el claim self-managed-country. */}
      {features.createPlanning && (
        <button
          className={`new-planning-fab${fabExpanded ? ' new-planning-fab--expanded' : ''}`}
          aria-label={t('createPlanning.newButton')}
          aria-expanded={fabExpanded}
          onPointerEnter={onFabPointerEnter}
          onPointerLeave={onFabPointerLeave}
          onPointerDown={onFabPointerDown}
          onClick={onFabClick}
        >
          <span className="new-planning-fab__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
            </svg>
          </span>
          <span className="new-planning-fab__label">{t('createPlanning.newButton')}</span>
        </button>
      )}

      <BottomNav />
    </div>
  );
}
